import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ArrowRight, ArrowLeft, LogOut, EyeOff } from 'lucide-react';
import { useTour } from '../src/contexts/TourContext';
import { useFocusTrap } from '../src/hooks/useFocusTrap';
import { getTourIcon } from '../src/lib/tourIcons';

/**
 * Overlay real de spotlight: oscurece toda la pantalla salvo un "agujero" recortado
 * exactamente sobre el elemento de la UI que el paso actual del tour señala (ver
 * src/contexts/TourContext.tsx para la resolución de qué elemento y cuándo). Portal a
 * document.body, mismo patrón que components/Modal.tsx (bloquea scroll del body mientras
 * está activo, cierra con Escape).
 *
 * The hole technique: a div positioned exactly over the target with
 * box-shadow: 0 0 0 9999px rgba(...) -- the giant shadow covers the rest of the screen without
 * needing SVG or clip-path. pointer-events: none on that div so a step with action:'navigate'
 * lets the user click on the real highlighted element.
 */

const RECT_PADDING = 8;
const TOOLTIP_MARGIN = 14;
const TOOLTIP_WIDTH = 340;

interface Rect { top: number; left: number; width: number; height: number; }

export const TourSpotlight: React.FC = () => {
  const { t } = useTranslation();
  const { isActive, currentStep, currentStepIndex, totalSteps, waitingForTarget, nextStep, prevStep, stopTour, jumpToStep, maxReachedIndex, confirmDismissFromExitPrompt, requestExitPrompt } = useTour();
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const holeRef = useRef<HTMLDivElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  useFocusTrap(tooltipRef, isActive && !waitingForTarget);

  // aria-live: anuncia el cambio de paso a lectores de pantalla sin que the user tenga que
  // re-navegar manualmente al tooltip -- el contenido del step (título+descripción) no se
  // anunciaba antes.
  useEffect(() => {
    if (!isActive || waitingForTarget || !currentStep) return;
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `${t(currentStep.titleKey)}. ${currentStep.usingFallback ? t('home.onboarding.fallbackNote') : t(currentStep.descriptionKey)}`;
    }
  }, [isActive, waitingForTarget, currentStep, t]);

  const selector = currentStep?.usingFallback ? (currentStep.fallbackSelector || currentStep.targetSelector) : currentStep?.targetSelector;
  const [clampOffset, setClampOffset] = useState<{ top: number; left: number } | null>(null);

  const recalc = useCallback(() => {
    if (!selector) { setTargetRect(null); return; }
    const el = document.querySelector(selector);
    if (!el) { setTargetRect(null); return; }
    const r = el.getBoundingClientRect();
    setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [selector]);

  useLayoutEffect(() => {
    if (!isActive || waitingForTarget) return;
    setClampOffset(null);
    recalc();
  }, [isActive, waitingForTarget, recalc, currentStepIndex]);

  // Clamp final contra el viewport real: computeTooltipPosition estima con una altura fija
  // (estTooltipHeight) porque el tooltip todavía no existe en el DOM en el momento del
  // cálculo -- un mensaje más largo que lo estimado (ej. the text de fallback) puede terminar
  // empujando el tooltip fuera de la pantalla igual. Tras el paint real, se mide el tooltip de
  // verdad y se corrige con un offset si se sale por cualquier borde -- red de seguridad
  // independiente de qué tan preciso sea el cálculo estimado previo.
  //
  // OJO: este efecto corre en un commit POSTERIOR al que resetea clampOffset a null (arriba) --
  // React no aplica setState de forma síncrona dentro del mismo efecto. Si se midiera
  // tooltipRef aquí sin descontar un clampOffset previo aún vigente en el DOM, el delta
  // calculated would be relative to a position ALREADY shifted by the previous clamp, cascading
  // error across steps (seen in production: a +2164px offset after several consecutive steps).
  // The currently applied clampOffset is explicitly subtracted before deciding the new delta,
  // so the calculation always starts from the "unclamped" position.
  useLayoutEffect(() => {
    if (!isActive || waitingForTarget || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const appliedTop = clampOffset?.top ?? 0;
    const appliedLeft = clampOffset?.left ?? 0;
    const unclamped = {
      top: rect.top - appliedTop,
      bottom: rect.bottom - appliedTop,
      left: rect.left - appliedLeft,
      right: rect.right - appliedLeft,
    };
    const margin = 12;
    let dx = 0, dy = 0;
    if (unclamped.bottom > window.innerHeight - margin) dy = window.innerHeight - margin - unclamped.bottom;
    if (unclamped.top < margin) dy = margin - unclamped.top;
    if (unclamped.right > window.innerWidth - margin) dx = window.innerWidth - margin - unclamped.right;
    if (unclamped.left < margin) dx = margin - unclamped.left;
    if (dx !== appliedLeft || dy !== appliedTop) setClampOffset(dx === 0 && dy === 0 ? null : { top: dy, left: dx });
  }, [isActive, waitingForTarget, targetRect, currentStepIndex]);

  useEffect(() => {
    if (!isActive || waitingForTarget) return;
    const onResize = () => recalc();
    const onScroll = () => recalc();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, { capture: true } as any);
    };
  }, [isActive, waitingForTarget, recalc]);

  // Bloquea scroll del body mientras el tour está activo -- same criteria as Modal.tsx.
  useEffect(() => {
    if (!isActive) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isActive]);

  // Guard anti-carrera: justo after nextStep()/prevStep() el DOM del tooltip anterior
  // puede seguir siendo el que ve el navegador en el instante del click del propio botón
  // "Siguiente" (el remount de la página tras navigate() y el re-render del tooltip no son
  // síncronos con el evento de click) -- sin este margen, el clic que avanza el tour podía
  // interpretarse a sí mismo como "clic fuera" y cerrar el tour de inmediato. La supresión dura
  // TODO el tiempo que waitingForTarget esté activo (cubre lazy-load lento en conexiones
  // reales, no un timer fijo que puede quedarse corto) más 300ms de margen tras resolverse,
  // para el propio remount del tooltip a su posición final.
  const suppressOutsideClickUntilRef = useRef(0);
  useEffect(() => {
    if (!isActive) return;
    if (waitingForTarget) {
      suppressOutsideClickUntilRef.current = Infinity;
    } else {
      suppressOutsideClickUntilRef.current = Date.now() + 300;
    }
  }, [isActive, waitingForTarget, currentStepIndex]);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { stopTour({ keepProgress: true }); return; }
      // Flechas solo cuando el foco no está en un campo de texto real de la página bajo el
      // tour (el step podría estar observando un formulario real, ej. action:'observe') --
      // así no se roban las flechas de un <input>/<textarea> que the user esté usando.
      const activeTag = (document.activeElement as HTMLElement | null)?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      if (e.key === 'ArrowRight') nextStep();
      else if (e.key === 'ArrowLeft') prevStep();
    };
    // A click outside the hole/tooltip (e.g. a real sidebar link) no longer silently closes the
    // tour -- it shows the same confirmation modal as the pathname navigation guard
    // (TourContext.tsx). When the step's target is a small, real element, that pathname guard
    // fires after react-router finishes navigating; this handler covers the synchronous instant
    // of the click event itself, before that route change.
    //
    // Si exitPromptPending ya está activo, este listener debe ignorar el clic por completo --
    // el modal de confirmación (TourExitConfirmModal, montado como hermano de este componente
    // en App.tsx) también Receives el clic vía bubbling hasta document, y sus botones ("Continuar
    // recorrido", etc.) NO están dentro de holeRef/tooltipRef -- sin este guard, cualquier clic
    // en ese modal se interpretaba como "otro clic fuera" y volvía a llamar requestExitPrompt(),
    // reabriendo el modal en el mismo tick en el que the user acababa de resolverlo.
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Un clic dentro del modal de confirmación de salida (TourExitConfirmModal, montado como
      // hermano de este componente) nunca debe re-disparar el prompt -- se detecta por DOM
      // (role="alertdialog") instead of por the status exitPromptPending, porque en el instante en
      // que este listener nativo corre (bubbling hasta document tras el onClick sintético de
      // React que ya llamó setExitPromptPending(false)) React todavía no re-renderizó, así que
      // cualquier ref/estado leído aquí seguiría reflejando the value VIEJO (true).
      if (target instanceof Element && target.closest('[role="alertdialog"]')) return;
      if (Date.now() < suppressOutsideClickUntilRef.current) return;
      if (holeRef.current?.contains(target)) return;
      if (tooltipRef.current?.contains(target)) return;
      requestExitPrompt();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isActive, stopTour, nextStep, prevStep, requestExitPrompt]);

  // Si el step action es 'navigate', un clic real dentro del hueco (sobre el elemento
  // resaltado) avanza el tour solo -- el listener de arriba ya no lo cierra porque
  // holeRef.current.contains(target) es true, así que este listener adicional es el único
  // punto que decide avanzar in that case específico.
  useEffect(() => {
    if (!isActive || currentStep?.action !== 'navigate') return;
    const el = selector ? document.querySelector(selector) : null;
    if (!el) return;
    const handleClick = () => nextStep();
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [isActive, currentStep?.action, selector, nextStep]);

  if (!isActive || !currentStep) return null;

  const isLast = currentStepIndex >= totalSteps - 1;

  const holeStyle: React.CSSProperties = targetRect ? {
    position: 'fixed',
    top: targetRect.top - RECT_PADDING,
    left: targetRect.left - RECT_PADDING,
    width: targetRect.width + RECT_PADDING * 2,
    height: targetRect.height + RECT_PADDING * 2,
    borderRadius: 16,
    boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.65)',
    pointerEvents: 'none',
    transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
    zIndex: 1001,
  } : {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', zIndex: 1001,
  };

  const baseTooltipStyle = computeTooltipPosition(targetRect, currentStep.placement);
  // El clamp se aplica como transform adicional instead of mutar top/left directamente -- así
  // conviven con el 'translate(-50%, -50%)' del caso centrado sin pisarse.
  const clampTransform = clampOffset ? ` translate(${clampOffset.left}px, ${clampOffset.top}px)` : '';
  const tooltipStyle: React.CSSProperties = {
    ...baseTooltipStyle,
    transform: `${baseTooltipStyle.transform || ''}${clampTransform}`,
  };

  const StepIcon = getTourIcon(currentStep.iconName);

  return createPortal(
    <>
      <div ref={holeRef} style={holeStyle} />
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-step-title"
        style={{ position: 'fixed', zIndex: 1002, width: TOOLTIP_WIDTH, maxWidth: 'calc(100vw - 32px)', transition: 'transform 0.15s ease', ...tooltipStyle }}
        className="bg-white dark:bg-slate-900 rounded-[1.75rem] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-fadeIn"
      >
        <div ref={liveRegionRef} aria-live="polite" aria-label={t('home.onboarding.liveRegionLabel')} className="sr-only" />
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black text-brand-600 uppercase tracking-widest">
              {t('home.onboarding.stepCounter', { current: currentStepIndex + 1, total: totalSteps })}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={confirmDismissFromExitPrompt}
                aria-label={t('home.onboarding.dontShowAgain')}
                data-tooltip={t('home.onboarding.dontShowAgain')}
                className="p-1.5 -m-1.5 text-slate-300 dark:text-slate-600 hover:text-rose-500 rounded-lg transition-colors"
              >
                <EyeOff size={14} />
              </button>
              <button onClick={() => stopTour({ keepProgress: true })} className="p-1.5 -m-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg transition-colors" aria-label={t('home.onboarding.exit')} data-tooltip={t('home.onboarding.exit')}>
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => {
              const reachable = i <= maxReachedIndex;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!reachable}
                  onClick={() => jumpToStep(i)}
                  aria-label={t('home.onboarding.goToStepAria', { step: i + 1, title: t(currentStep.titleKey) })}
                  aria-current={i === currentStepIndex ? 'step' : undefined}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${reachable ? 'cursor-pointer' : 'cursor-default'} ${i <= currentStepIndex ? 'bg-brand-600 dark:bg-blue-400' : reachable ? 'bg-blue-200 dark:bg-blue-500/30 hover:bg-blue-300 dark:hover:bg-blue-500/50' : 'bg-slate-100 dark:bg-slate-800'}`}
                />
              );
            })}
          </div>

          {waitingForTarget ? (
            <div className="min-h-[70px] flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-brand-600/20 border-t-brand-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="min-h-[70px]">
              <div className="flex items-start gap-2.5 mb-1.5">
                <span className="mt-0.5 shrink-0 w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-brand-600 flex items-center justify-center">
                  <StepIcon size={16} />
                </span>
                <h3 id="tour-step-title" className="text-base font-black text-slate-800 dark:text-slate-100 pt-1">{t(currentStep.titleKey)}</h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-[42px]">
                {currentStep.usingFallback ? t('home.onboarding.fallbackNote') : t(currentStep.descriptionKey)}
              </p>
              {currentStep.action === 'navigate' && !currentStep.usingFallback && (
                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mt-2 pl-[42px]">
                  {t('home.onboarding.clickHereHint')}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              data-tour-control="prev"
              onClick={prevStep}
              disabled={currentStepIndex === 0}
              className="label-field flex items-center gap-1 px-3 py-2.5 rounded-xl hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-0 transition-all"
            >
              <ArrowLeft size={13} /> {t('home.onboarding.back')}
            </button>
            <button
              data-tour-control="exit"
              onClick={() => stopTour({ keepProgress: false })}
              className="label-field flex items-center gap-1 px-3 py-2.5 rounded-xl hover:text-rose-500 transition-all"
            >
              <LogOut size={13} /> {t('home.onboarding.exit')}
            </button>
            <button
              data-tour-control="next"
              onClick={nextStep}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-brand-600 text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-[#00509d] transition-all shadow-lg shadow-blue-900/20"
            >
              {isLast ? t('home.onboarding.finish') : t('home.onboarding.next')} <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

/** Elige la posición del tooltip according to el espacio disponible: prueba bottom -> top -> right ->
 * left (para placement 'auto') y usa la primera que no se salga del viewport; si placement
 * viene fijo, respeta esa preferencia sin probar alternativas. Sin targetRect (fallback total,
 * ej. selector no encontrado en absoluto) centra el tooltip en la pantalla. */
function computeTooltipPosition(targetRect: Rect | null, placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto'): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!targetRect) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const estTooltipHeight = 260;

  // Target sin borde útil visible (ej. [data-tour-id="page-root"]: puede medir menos del
  // 70% del viewport y aun así tener su borde inferior/superior fuera de lo visible, o casi
  // pegado al borde de la pantalla sin dejar espacio real para el tooltip) -- instead of
  // adivinar por proporción de tamaño, is calculated directamente si CUALQUIERA de los 4 lados
  // deja the margin mínimo real (tooltip completo + su propio margen) dentro del viewport. Si
  // ninguno lo hace, se centra en pantalla instead of forzar una posición que lo corte.
  const fits = {
    bottom: targetRect.top + targetRect.height + RECT_PADDING + TOOLTIP_MARGIN + estTooltipHeight <= vh,
    top: targetRect.top - RECT_PADDING - TOOLTIP_MARGIN - estTooltipHeight >= 0,
    right: targetRect.left + targetRect.width + RECT_PADDING + TOOLTIP_MARGIN + TOOLTIP_WIDTH <= vw,
    left: targetRect.left - RECT_PADDING - TOOLTIP_MARGIN - TOOLTIP_WIDTH >= 0,
  };

  const order: ('bottom' | 'top' | 'right' | 'left')[] = placement && placement !== 'auto' ? [placement] : ['bottom', 'top', 'right', 'left'];
  const chosen = order.find(p => fits[p]);

  // Ningún lado cabe -- típicamente un target enorme (ej. el contenedor "page-root" de una
  // página completa, usado como fallback universal) donde no hay borde útil para anclar un
  // tooltip adyacente. instead of forzar 'bottom' y empujarlo fuera del viewport, se centra en
  // pantalla como the case sin targetRect.
  if (!chosen) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const clampLeft = (left: number) => Math.min(Math.max(left, 12), vw - TOOLTIP_WIDTH - 12);

  switch (chosen) {
    case 'top':
      return { top: targetRect.top - RECT_PADDING - TOOLTIP_MARGIN, left: clampLeft(targetRect.left), transform: 'translateY(-100%)' };
    case 'right':
      return { top: Math.min(targetRect.top, vh - estTooltipHeight - 12), left: targetRect.left + targetRect.width + RECT_PADDING + TOOLTIP_MARGIN };
    case 'left':
      return { top: Math.min(targetRect.top, vh - estTooltipHeight - 12), left: targetRect.left - RECT_PADDING - TOOLTIP_MARGIN - TOOLTIP_WIDTH };
    case 'bottom':
    default:
      return { top: targetRect.top + targetRect.height + RECT_PADDING + TOOLTIP_MARGIN, left: clampLeft(targetRect.left) };
  }
}

export default TourSpotlight;
